
import { useState, useEffect } from 'react';

import downloadicon from '../img/downloadicon.svg';
import downloadicondone from '../img/downloadicon-done.svg';
import infoicon from '../img/icon-info.svg';

import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

import { translate } from '../functions/translationHelper';
import {
  deleteTrailPackageCache,
  getPackageKey,
  precacheTrailPackage,
  saveOfflinePackage,
} from '../functions/offlineStore';

function OfflineDownloadButton(props) {

  const [progress, setProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [fullyLoaded, setFullyLoaded] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const [showInfoBox, setShowinfobox] = useState(false);

  function getMapPackageUrls() {
    if (!process.env.REACT_APP_MAP_KEY) {
      return [];
    }

    return [
      `https://api.maptiler.com/maps/streets/style.json?key=${process.env.REACT_APP_MAP_KEY}`,
    ];
  }

  async function fetchTrailPackage(client, trail, lang) {
    const params = new URLSearchParams({
      client,
      trail,
      lang,
      minMapZoom: '0',
      maxMapZoom: '15',
      maxMapTiles: '1500',
    });

    if (process.env.REACT_APP_MAP_KEY) {
      params.set('mapkey', process.env.REACT_APP_MAP_KEY);
    }

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}offline-getServerfiles.php?${params.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Could not load offline package');
    }

    const data = await response.json();

    if (data?.error) {
      throw new Error(data.error);
    }

    const metadata = {
      client,
      trail,
      lang,
      cacheName: `trail-package-${trail}-${lang}`,
      packageKey: getPackageKey(client, trail, lang),
      ...(data.metadata || {}),
    };

    return {
      ...data,
      files: data.files || [],
      optionalFiles: getMapPackageUrls(),
      apiUrls: data.apiUrls || [],
      apiSnapshots: data.apiSnapshots || [],
      metadata,
    };
  }

  async function cacheTrailAssets(client, trail, lang) {
    try {
      setDownloadError(null);
      setIsDownloading(true);
      setProgress(0);

      const trailPackage = await fetchTrailPackage(client, trail, lang);
      const result = await precacheTrailPackage(trailPackage, ({ completed, total }) => {
        setProgress(total > 0 ? Math.round((completed / total) * 100) : 100);
      });
      const criticalFailed = Array.isArray(result?.criticalFailed) ? result.criticalFailed : (result?.failed || []);
      const optionalFailed = result?.optionalFailed || [];

      if (criticalFailed.length > 0) {
        console.error('Critical offline cache failures:', criticalFailed);
        throw new Error('Critical offline files could not be downloaded');
      }

      if (optionalFailed.length > 0) {
        console.warn('Optional offline cache failures:', optionalFailed);
      }

      await saveOfflinePackage(trailPackage);

      console.log("All offline trail assets preloaded!");
      setProgress(100);
      setFullyLoaded(true);
      localStorage.setItem(`offline-${trail}-${lang}`, 1);

      if (trailPackage?.metadata?.mapTiles?.maxZoom !== undefined) {
        localStorage.setItem(`offline-map-maxzoom-${trail}-${lang}`, trailPackage.metadata.mapTiles.maxZoom);
      }
    } catch (error) {
      console.error('Offline package failed:', error);
      setDownloadError(translate("downloaderror_retry", props.lang));
      setFullyLoaded(false);
      localStorage.removeItem(`offline-${trail}-${lang}`);
    } finally {
      setIsDownloading(false);
    }
  }

  async function deleteTrailCache(client, trail, lang) {
    await deleteTrailPackageCache({ client, trail, lang });
    localStorage.removeItem(`offline-${trail}-${lang}`);
    setFullyLoaded(false);
    setProgress(0);
    setDownloadError(null);
  }

  function handleDeleteClick() {
    const confirmed = window.confirm(translate("deletedownload", props.lang));
    if (confirmed) {
      deleteTrailCache(props.client, props.trail, props.lang);
    }
  }

  useEffect(() => {
    if (localStorage.getItem(`offline-${props.trail}-${props.lang}`) == 1) {
      setFullyLoaded(true);
    } else {
      setFullyLoaded(false);
    }
    setShowinfobox(false);
    setDownloadError(null);
  }, [props.trail, props.lang])

  return (
    <div className="offlinedownloadcontainer">
      <div className="offlinedownloadbuttongroup">
        {isDownloading ?
          <div className="button-secondary clickable offlinedownloadbutton" style={{ borderColor: "transparent" }}>
            <CircularProgressbar
              value={progress}
              strokeWidth={16}
              className='offlinebutton-progress'
              styles={{
                path: {
                  stroke: 'var(--main-color)',
                },
                trail: {
                  stroke: '#e3e3e3',
                },
              }}
            />
            {progress}% {translate("percentloaded", props.lang)}
          </div>
          :
          (
            fullyLoaded ?
              <div className="button-secondary clickable offlinedownloadbutton" style={{ borderColor: "transparent" }} onClick={() => handleDeleteClick()}>
                <img src={downloadicondone} />
                {translate("contentloaded", props.lang)}
              </div>
              :
              <div className="button-secondary clickable offlinedownloadbutton" onClick={() => cacheTrailAssets(props.client, props.trail, props.lang)}>
                <img src={downloadicon} />
                <span>{translate("downloadcontent", props.lang)}</span>
                {props.recommended && <span className="offlinedownload-recommended-label">{translate("recommended", props.lang)}</span>}
              </div>
          )
        }
        <div className='button-info clickable' onClick={() => setShowinfobox(!showInfoBox)}>
          <img src={infoicon} />
        </div>
      </div>
      {
        showInfoBox &&
        <div className='downloadinfobox'>{translate("downloadinfo", props.lang)}</div>
      }
      {
        downloadError &&
        <div className='downloadinfobox'>{downloadError}</div>
      }
    </div>
  );
}


export default OfflineDownloadButton;
